import { useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheck, Shield, Bot, Braces, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useMyServices } from '@/api/services';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackServiceActivate } from '@/lib/analytics-events';
import { WAAP_PROVIDER, WAAP_PRODUCT } from './waap-service';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * WAAP onboarding entry for the utm_campaign=waf_free funnel.
 *
 * Activates a standalone WAAP service (the `cobra` provider), then hands off to
 * the WAAP quick-setup wizard at /services/waap/:id/onboarding (Domain & Origin →
 * SSL → Security Features → provision). If the account already has a WAAP
 * service, activation is skipped and we go straight to its wizard.
 *
 * Uses the same activation-hero visual language as the in-catalog WAAP landing
 * (WaapActivateLanding) so the funnel entry and catalog entry look identical.
 */
export function WaapOnboardingLauncher() {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();

  const { data: services = [], isLoading: servicesLoading } = useMyServices();
  const existingWaap = useMemo(
    () => services.find((s) => s.provider?.name?.trim().toLowerCase() === WAAP_PROVIDER),
    [services],
  );

  const { data: providers = [], isLoading: providersLoading } = useProviders();
  // Only an active cobra provider can be provisioned self-serve.
  const cobra = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === WAAP_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  const activate = useActivateService();

  // Already have a WAAP service → skip activation, resume its quick-setup wizard.
  if (!servicesLoading && existingWaap) {
    return <Navigate to={`/services/waap/${existingWaap.id}/onboarding`} replace />;
  }

  const email = user?.email ?? '';
  const loading = servicesLoading || providersLoading;
  const unavailable = !providersLoading && !cobra;
  const canActivate = !!cobra && !!email && !activate.isPending;

  function handleActivate() {
    if (!cobra || !canActivate) return;
    activate.mutate(
      { serviceName: WAAP_PRODUCT.serviceName, providerId: cobra.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(WAAP_PROVIDER, 'waap');
          navigate(`/services/waap/${service.id}/onboarding`, { replace: true });
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate WAAP. Please try again.',
          }),
      },
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Web Application &amp; API Protection
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description="AI-WAF, bot management, rate limiting and API security for your websites."
      />

      {loading ? (
        <Skeleton style={{ height: 200 }} />
      ) : unavailable ? (
        <Alert tone="warning" title={`${WAAP_PRODUCT.displayName} is not available yet`}>
          {WAAP_PRODUCT.displayName} is not available for self-serve activation on your account yet.
          Please contact our team to enable it.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <ShieldCheck size={30} />
          </span>

          <p style={heroEyebrow}>Web Application &amp; API Protection</p>
          <h2 style={heroTitle}>Activate WAAP for your organization</h2>
          <p style={heroLede}>
            Provision a protection edge, then a guided setup walks you through adding your website,
            issuing SSL and turning on AI-WAF — it only takes a moment.
          </p>

          <Button
            size="lg"
            loading={activate.isPending}
            startIcon={<Sparkles size={16} />}
            onClick={handleActivate}
            disabled={!canActivate}
            style={{ marginTop: 'var(--vn-space-2)' }}
          >
            {activate.isPending ? 'Activating…' : 'Activate Web Application & API Protection'}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {PERKS.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Container>
  );
}

const PERKS = [
  { icon: <Shield size={16} />, title: 'AI-WAF', desc: 'AI-driven web application firewall blocks OWASP and zero-day attacks.' },
  { icon: <Bot size={16} />, title: 'Bot management', desc: 'Detect and mitigate malicious bots, scrapers and automated abuse.' },
  { icon: <Braces size={16} />, title: 'API protection', desc: 'Discover your APIs and guard them against abuse and data loss.' },
];

export default WaapOnboardingLauncher;
